import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkles, MapPin, Clock, Wrench, Loader2, Send, CheckCircle2, Info, Lock,
} from "lucide-react";

const URG = { urgent: "Urgent", flexible: "Flexible", planned: "Planifié" };

export default function ProOpportunities() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [form, setForm] = useState({ message: "", price_estimate: "" });
  const [sending, setSending] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/devis/pro/opportunities").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const submit = async (reqId) => {
    if ((form.message || "").trim().length < 5) { toast.error("Votre proposition est trop courte."); return; }
    setSending(true);
    try {
      await api.post(`/devis/pro/opportunities/${reqId}/respond`, form);
      toast.success("Proposition envoyée ! Le client a reçu vos coordonnées.");
      setOpenId(null); setForm({ message: "", price_estimate: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  const items = data?.items || [];

  return (
    <div data-testid="pro-opportunities-page">
      <div className="flex items-start gap-3 mb-1">
        <Sparkles className="text-[#E05638] mt-1" size={22} />
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-[#172A3A]">Opportunités</h1>
          <p className="text-sm text-slate-500">
            Demandes de votre métier publiées depuis {data?.min_age_days ?? 3} jours et n'ayant pas encore reçu {data?.max_proposals ?? 3} propositions. Répondez pour débloquer le contact du client.
          </p>
        </div>
      </div>

      {data?.quota_reached && (
        <div className="mt-4 flex items-center gap-2 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm px-4 py-3" data-testid="pro-opp-quota">
          <Info size={16} /> Vous avez atteint votre quota de demandes du jour. Revenez demain pour saisir de nouvelles opportunités.
        </div>
      )}

      {loading ? (
        <div className="mt-10 flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin" size={18} /> Chargement…</div>
      ) : items.length === 0 ? (
        <div className="mt-10 text-center py-16 text-slate-500 border border-dashed border-slate-200 rounded-2xl" data-testid="pro-opp-empty">
          <CheckCircle2 className="mx-auto text-emerald-500 mb-3" size={32} />
          Aucune demande en attente pour vos métiers en ce moment. Revenez bientôt !
        </div>
      ) : (
        <div className="mt-6 space-y-4" data-testid="pro-opp-list">
          {items.map((r) => (
            <div key={r.id} className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={`pro-opp-card-${r.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="font-heading text-lg font-bold text-[#172A3A] flex items-center gap-2">
                    <Wrench size={16} className="text-[#E05638]" /> {r.trade_name}{r.subneed ? ` · ${r.subneed}` : ""}
                  </div>
                  <div className="mt-1 flex items-center gap-3 flex-wrap text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1"><MapPin size={12} /> {r.city}{r.postal_code ? ` (${r.postal_code})` : ""}</span>
                    <span className="inline-flex items-center gap-1"><Clock size={12} /> Publiée il y a {r.age_days} j</span>
                    {r.requester_type && <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-semibold">{r.requester_type === "pro" ? "Professionnel" : "Particulier"}</span>}
                    {r.urgency && <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{URG[r.urgency] || r.urgency_label}</span>}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-orange-50 border border-orange-200 text-[#E05638] text-xs font-bold" data-testid={`pro-opp-slots-${r.id}`}>
                    {r.proposals_count}/{r.proposals_max} propositions
                  </span>
                  <div className="mt-1 text-[11px] text-emerald-600 font-semibold">{r.slots_left} place{r.slots_left > 1 ? "s" : ""} restante{r.slots_left > 1 ? "s" : ""}</div>
                </div>
              </div>

              {r.description && <p className="mt-3 text-sm text-slate-700 whitespace-pre-line">{r.description}</p>}

              <div className="mt-3 flex items-center gap-1.5 text-xs text-slate-400">
                <Lock size={12} /> Les coordonnées du client s'afficheront après l'envoi de votre proposition.
              </div>

              {openId === r.id ? (
                <div className="mt-4 border-t border-slate-100 pt-4 space-y-3" data-testid={`pro-opp-form-${r.id}`}>
                  <textarea rows={4} value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                    placeholder="Présentez votre proposition au client (disponibilité, approche, délai…)"
                    data-testid={`pro-opp-message-${r.id}`}
                    className="w-full text-sm border border-slate-200 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-[#E05638]/40" />
                  <input value={form.price_estimate} onChange={(e) => setForm((f) => ({ ...f, price_estimate: e.target.value }))}
                    placeholder="Estimation de prix (facultatif) — ex. : à partir de 150 €"
                    data-testid={`pro-opp-price-${r.id}`}
                    className="w-full text-sm border border-slate-200 rounded-xl p-2.5 focus:outline-none focus:ring-2 focus:ring-[#E05638]/40" />
                  <div className="flex gap-2">
                    <button onClick={() => submit(r.id)} disabled={sending} data-testid={`pro-opp-submit-${r.id}`}
                      className="inline-flex items-center gap-2 h-10 px-5 rounded-xl bg-[#E05638] text-white font-bold text-sm hover:bg-[#c94a2f] disabled:opacity-50">
                      {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Envoyer ma proposition
                    </button>
                    <button onClick={() => { setOpenId(null); setForm({ message: "", price_estimate: "" }); }}
                      className="h-10 px-4 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">Annuler</button>
                  </div>
                </div>
              ) : (
                <button onClick={() => { setOpenId(r.id); setForm({ message: "", price_estimate: "" }); }}
                  disabled={data?.quota_reached}
                  data-testid={`pro-opp-respond-${r.id}`}
                  className="mt-4 inline-flex items-center gap-2 h-10 px-5 rounded-xl bg-[#172A3A] text-white font-semibold text-sm hover:bg-[#223c52] disabled:opacity-50">
                  <Send size={15} /> Répondre à cette demande
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
